import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { ActivatedRoute, Router } from '@angular/router';
import { PetsService } from '../../../../services/pets';
import { DeviceDetectorService } from '../../../../services/device-detector.service';

type VaccinationDateControl = 'vaccination_date' | 'next_due_date';

interface VaccinationCalendarState {
  show: boolean;
  viewDate: Date;
}

interface VaccinationCalendarDay {
  date: Date;
  dayNumber: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

@Component({
  selector: 'app-petvaccinations',
  templateUrl: './petvaccinations.component.html',
  styleUrls: ['./petvaccinations.component.scss']
})
export class PetvaccinationsComponent implements OnInit {

  allData: any[] = [];

  displayedColumns: string[] = [
    'vaccine_name',
    'manufacturer',
    'batch_number',
    'vaccination_date',
    'next_due_date',
    'authorized_vet',
    'notes',
    'actions'
  ];

  dataSource = new MatTableDataSource<any>([]);

  contentReady: boolean = true;
  noData: boolean = false;

  vaccinationForm!: FormGroup;

  editMode: boolean = false;
  selectedVaccinationId: number | null = null;

  petId!: number;
  isMobile!: boolean;
  isAr: boolean = false;
  submitted: boolean = false;

  todayDateOnly = this.formatDateOnly(new Date());

  calendarState: Record<VaccinationDateControl, VaccinationCalendarState> = {
    vaccination_date: {
      show: false,
      viewDate: new Date()
    },
    next_due_date: {
      show: false,
      viewDate: new Date()
    }
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private petsService: PetsService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private deviceService: DeviceDetectorService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem('language') == 'ar';

    if (this.isMobile) {
      this.router.navigateByUrl('/petvaccinations.mobile');
      return;
    }

    this.initForm();

    this.route.queryParams.subscribe(params => {
      if (params['petId']) {
        this.petId = +params['petId'];
        this.loadVaccinations();
      } else {
        this.contentReady = false;
        this.noData = true;
      }
    });
  }

  /** Initialize Form */
  initForm(): void {
    this.vaccinationForm = this.fb.group({
      vaccine_name: ['', [Validators.required, Validators.minLength(2)]],
      manufacturer: [''],
      batch_number: [''],
      vaccination_date: [this.todayDateOnly, Validators.required],
      next_due_date: [''],
      authorized_vet: [''],
      notes: ['']
    });

    this.refreshCalendarViewsFromForm();
  }

  shouldShowError(controlName: string): boolean {
    const control = this.vaccinationForm?.get(controlName);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  getErrorMessage(controlName: string, label: string): string {
    const control = this.vaccinationForm?.get(controlName);

    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.isAr ? `${label} مطلوب` : `${label} is required`;
    }

    if (control.errors['minlength']) {
      const requiredLength = control.errors['minlength'].requiredLength;
      return this.isAr
        ? `${label} لازم يكون ${requiredLength} حروف على الاقل`
        : `${label} must be at least ${requiredLength} characters`;
    }

    return this.isAr ? `${label} غير صحيح` : `${label} is invalid`;
  }

  getMissingRequiredCount(): number {
    if (!this.vaccinationForm) {
      return 0;
    }

    return Object.keys(this.vaccinationForm.controls).filter((controlName) => {
      const control = this.vaccinationForm.get(controlName);
      return control?.hasError('required');
    }).length;
  }

  private validateBeforeSubmit(): boolean {
    this.submitted = true;

    if (this.vaccinationForm.invalid) {
      this.vaccinationForm.markAllAsTouched();

      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'تأكد من بيانات التطعيم' : 'Please check vaccination data',
        text: this.isAr
          ? 'راجع الحقول المطلوبة قبل الحفظ'
          : 'Review the required fields before saving',
        confirmButtonText: this.isAr ? 'تمام' : 'OK',
      });

      return false;
    }

    return true;
  }

  /** Load Vaccinations by Pet ID */
  loadVaccinations(): void {
    if (!this.petId) return;

    this.contentReady = true;

    this.petsService.getVaccinationsByPetId(this.petId).subscribe({
      next: (data) => {
        this.allData = data || [];
        this.dataSource = new MatTableDataSource(this.allData);

        if (this.paginator) {
          this.dataSource.paginator = this.paginator;
        }

        this.noData = this.allData.length === 0;
        this.contentReady = false;
      },
      error: (err) => {
        console.error('❌ Error loading vaccinations:', err);
        this.contentReady = false;
        this.noData = true;
      },
    });
  }

  /** Save or Update Vaccination */
  saveVaccination(): void {
    if (!this.validateBeforeSubmit()) {
      return;
    }

    const raw = this.vaccinationForm.getRawValue();

    const payload = {
      ...raw,
      vaccination_date: this.normalizeDateValue(raw.vaccination_date),
      next_due_date: raw.next_due_date ? this.normalizeDateValue(raw.next_due_date) : '',
      pet_id: this.petId
    };

    if (this.editMode && this.selectedVaccinationId) {
      this.petsService.updateVaccination(this.selectedVaccinationId, payload).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: this.isAr ? 'تم التحديث' : 'Updated',
            text: this.isAr ? 'تم تعديل بيانات التطعيم بنجاح' : 'Vaccination updated successfully',
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });

          this.resetForm();
          this.loadVaccinations();
        },
        error: (err) => {
          console.error(err);

          Swal.fire({
            icon: 'error',
            title: this.isAr ? 'خطأ' : 'Error',
            text: this.isAr ? 'حدث خطأ أثناء التعديل' : 'Failed to update vaccination',
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });
        },
      });
    } else {
      this.petsService.addVaccination(payload).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: this.isAr ? 'تم الحفظ' : 'Saved',
            text: this.isAr ? 'تمت إضافة التطعيم بنجاح' : 'Vaccination added successfully',
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });

          this.resetForm();
          this.loadVaccinations();
        },
        error: (err) => {
          console.error(err);

          Swal.fire({
            icon: 'error',
            title: this.isAr ? 'خطأ' : 'Error',
            text: this.isAr ? 'تعذر إضافة التطعيم' : 'Failed to add vaccination',
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });
        },
      });
    }
  }

  /** Edit Vaccination */
  editVaccination(vaccination: any): void {
    this.editMode = true;
    this.selectedVaccinationId = vaccination.id;

    this.vaccinationForm.patchValue({
      vaccine_name: vaccination.vaccine_name || '',
      manufacturer: vaccination.manufacturer || '',
      batch_number: vaccination.batch_number || '',
      vaccination_date: this.normalizeDateValue(vaccination.vaccination_date) || this.todayDateOnly,
      next_due_date: vaccination.next_due_date ? this.normalizeDateValue(vaccination.next_due_date) : '',
      authorized_vet: vaccination.authorized_vet || '',
      notes: vaccination.notes || ''
    });

    this.refreshCalendarViewsFromForm();

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /** Delete Vaccination */
  deleteVaccination(id: number): void {
    Swal.fire({
      title: this.isAr ? 'هل أنت متأكد؟' : 'Are you sure?',
      text: this.isAr ? 'لن يمكنك التراجع بعد الحذف!' : 'You will not be able to undo this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.isAr ? 'نعم، احذف!' : 'Yes, delete!',
      cancelButtonText: this.isAr ? 'إلغاء' : 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.petsService.deleteVaccination(id).subscribe({
          next: () => {
            Swal.fire({
              icon: 'success',
              title: this.isAr ? 'تم الحذف!' : 'Deleted!',
              text: this.isAr ? 'تم حذف التطعيم بنجاح.' : 'Vaccination deleted successfully.',
              confirmButtonText: this.isAr ? 'تمام' : 'OK',
            });

            this.loadVaccinations();
          },
          error: (err) => {
            console.error(err);

            Swal.fire({
              icon: 'error',
              title: this.isAr ? 'خطأ' : 'Error',
              text: this.isAr ? 'تعذر حذف التطعيم' : 'Failed to delete vaccination',
              confirmButtonText: this.isAr ? 'تمام' : 'OK',
            });
          },
        });
      }
    });
  }

  /** Filter table */
  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  /** Check if date is upcoming within 30 days */
  isUpcoming(dateString: string | Date): boolean {
    if (!dateString) return false;

    const nextDue = this.toSafeDate(dateString);
    if (!nextDue) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    nextDue.setHours(0, 0, 0, 0);

    return nextDue >= today && nextDue <= thirtyDaysFromNow;
  }

  /** Check if date is overdue */
  isOverdue(dateString: string | Date): boolean {
    if (!dateString) return false;

    const nextDue = this.toSafeDate(dateString);
    if (!nextDue) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    nextDue.setHours(0, 0, 0, 0);

    return nextDue < today;
  }

  /** Reset form to initial state */
  resetForm(): void {
    this.submitted = false;
    this.editMode = false;
    this.selectedVaccinationId = null;

    this.vaccinationForm.reset({
      vaccine_name: '',
      manufacturer: '',
      batch_number: '',
      vaccination_date: this.todayDateOnly,
      next_due_date: '',
      authorized_vet: '',
      notes: ''
    });

    this.refreshCalendarViewsFromForm();
  }

  /* =========================================================
     Reusable Calendar Methods
     استخدم نفس الميثودز دي في أي view فيها date field
     ========================================================= */

  toggleDateCalendar(controlName: VaccinationDateControl): void {
    const willOpen = !this.calendarState[controlName].show;

    this.closeAllDateCalendars(controlName);

    this.calendarState[controlName].show = willOpen;

    if (willOpen) {
      const selectedDate = this.parseDateOnly(this.vaccinationForm.get(controlName)?.value);
      const baseDate = selectedDate || new Date();

      this.calendarState[controlName].viewDate = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        1
      );
    }
  }

  closeDateCalendar(controlName: VaccinationDateControl): void {
    this.calendarState[controlName].show = false;
  }

  closeAllDateCalendars(except?: VaccinationDateControl): void {
    (['vaccination_date', 'next_due_date'] as VaccinationDateControl[]).forEach((controlName) => {
      if (controlName !== except) {
        this.calendarState[controlName].show = false;
      }
    });
  }

  previousDateCalendarMonth(controlName: VaccinationDateControl): void {
    const currentViewDate = this.calendarState[controlName].viewDate;

    this.calendarState[controlName].viewDate = new Date(
      currentViewDate.getFullYear(),
      currentViewDate.getMonth() - 1,
      1
    );
  }

  nextDateCalendarMonth(controlName: VaccinationDateControl): void {
    const currentViewDate = this.calendarState[controlName].viewDate;

    this.calendarState[controlName].viewDate = new Date(
      currentViewDate.getFullYear(),
      currentViewDate.getMonth() + 1,
      1
    );
  }

  getDateCalendarTitle(controlName: VaccinationDateControl): string {
    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      month: 'long',
      year: 'numeric',
    }).format(this.calendarState[controlName].viewDate);
  }

  getDateCalendarWeekDays(): string[] {
    return this.isAr
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }

  getDateCalendarDays(controlName: VaccinationDateControl): VaccinationCalendarDay[] {
    const viewDate = this.calendarState[controlName].viewDate;
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - firstDay.getDay());

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selectedDate = this.parseDateOnly(this.vaccinationForm.get(controlName)?.value);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      date.setHours(0, 0, 0, 0);

      return {
        date,
        dayNumber: this.isAr ? this.toArabicDigits(date.getDate()) : String(date.getDate()),
        isCurrentMonth: date.getMonth() === month,
        isToday: this.isSameDate(date, today),
        isSelected: selectedDate ? this.isSameDate(date, selectedDate) : false,

        // في التطعيمات مش بنقفل الأيام القديمة عشان Vaccination History ممكن يكون قديم
        isDisabled: false,
      };
    });
  }

  selectDateCalendarDate(controlName: VaccinationDateControl, date: Date): void {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    this.vaccinationForm.patchValue({
      [controlName]: this.formatDateOnly(selectedDate)
    });

    this.vaccinationForm.get(controlName)?.markAsTouched();
    this.vaccinationForm.get(controlName)?.updateValueAndValidity();

    this.closeDateCalendar(controlName);
  }

  getDateDisplay(controlName: VaccinationDateControl, placeholder: string): string {
    const date = this.parseDateOnly(this.vaccinationForm?.get(controlName)?.value);

    if (!date) {
      return placeholder;
    }

    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  private refreshCalendarViewsFromForm(): void {
    (['vaccination_date', 'next_due_date'] as VaccinationDateControl[]).forEach((controlName) => {
      const date = this.parseDateOnly(this.vaccinationForm?.get(controlName)?.value);
      const baseDate = date || new Date();

      this.calendarState[controlName] = {
        show: false,
        viewDate: new Date(baseDate.getFullYear(), baseDate.getMonth(), 1)
      };
    });
  }

  private normalizeDateValue(value: any): string {
    if (!value) return '';

    const parsedDate = this.parseDateOnly(value);
    if (parsedDate) {
      return this.formatDateOnly(parsedDate);
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return this.formatDateOnly(date);
    }

    return String(value);
  }

  private parseDateOnly(value: any): Date | null {
    if (!value) return null;

    const cleanValue = String(value)
      .replace('T', ' ')
      .replace('Z', '')
      .split('.')[0]
      .trim()
      .split(' ')[0];

    const match = cleanValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!match) return null;

    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
  }

  private toSafeDate(value: any): Date | null {
    const dateOnly = this.parseDateOnly(value);
    if (dateOnly) return dateOnly;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private isSameDate(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
  }

  private formatDateOnly(date: Date): string {
    const pad = (num: number) => String(num).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private toArabicDigits(value: string | number): string {
    return String(value).replace(/\d/g, (digit) => '٠١٢٣٤٥٦٧٨٩'[Number(digit)]);
  }
}